import { requestData, requestHeaders } from './api-request-data'
import { getLastSuccessfulDate, setLastSuccessfulDate } from './session-storage'

// Define a constant for the API endpoint
const API_ENDPOINT = 'https://www.krl.dk/sirka/sirkaApi/tableApi'

export type InstitutionType = 'daycare' | 'schoolAgeChildcare'

const fetchDataFromAPI = async (
  institutionType: InstitutionType,
  seniority: number,
) => {
  // Function to calculate the previous month and year
  const getPreviousMonth = (
    year: number,
    month: number,
  ): { year: number; month: number } => {
    // Calculate the previous month
    if (month === 1) {
      year -= 1 // Go back one year
      month = 12 // Set month to December
    } else {
      month -= 1 // Just go back one month
    }

    // Convert the year and month to strings
    const previousMonthStr = month.toString().padStart(2, '0') // Ensure two digits

    return { year, month: parseInt(previousMonthStr, 10) }
  }

  const currentDate = new Date()
  let currentYear = currentDate.getFullYear()
  let currentMonth = currentDate.getMonth() + 1
  currentMonth = parseInt(currentMonth.toString().padStart(2, '0')) // Ensure two digits

  // Start with previous month
  let prevMonthYear = getPreviousMonth(currentYear, currentMonth)
  currentYear = prevMonthYear.year
  currentMonth = prevMonthYear.month

  // Try to get the last successful month and year from session storage
  const lastSuccessfulDate = getLastSuccessfulDate()

  if (lastSuccessfulDate) {
    currentYear = lastSuccessfulDate.year
    currentMonth = lastSuccessfulDate.month
  }

  for (let attempts = 0; attempts < 5; attempts += 1) {
    try {
      const requestDataByType = {
        daycare: requestData.daycare,
        schoolAgeChildcare: requestData.schoolAgeChildcare,
      }

      const selectedRequestData = requestDataByType[institutionType]

      if (!selectedRequestData) {
        throw new Error(`Invalid institutionType: ${institutionType}`)
      }

      // Update the 'anc' value in the selection filters based on the seniority parameter
      const selection = selectedRequestData.selection[0]
      if (!selection) {
        throw new Error('Missing selection data')
      }
      selection.filters.anc = [seniority.toString()]

      // Update the 'time' value in the request data with the current month and year
      selectedRequestData.time = [
        {
          y1: currentYear.toString(),
          m1: currentMonth.toString().padStart(2, '0'),
        },
      ]

      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify(selectedRequestData),
      })

      if (!response.ok) {
        throw new Error(`API status: ${response.status}`)
      }

      setLastSuccessfulDate(currentYear, currentMonth)
      // eslint-disable-next-line no-await-in-loop
      const data = await response.json()

      return { data, selectedRequestData }
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error(`Attempt ${attempts + 1} failed: ${error.message}`)

        // Check if the error message contains 'API status'
        if (error.message.includes('API status')) {
          const match = error.message.match(/\d+/)
          if (match) {
            // Extract the status code from the message
            const statusCode = match[0]
            throw new Error(`Fetch failed with status code: ${statusCode}`)
          } else {
            throw new Error(
              `Fetch failed with unknown status code ${error.message}`,
            )
          }
        }

        if (attempts < 4) {
          prevMonthYear = getPreviousMonth(currentYear, currentMonth)
          currentYear = prevMonthYear.year
          currentMonth = prevMonthYear.month
        } else {
          throw new Error(
            `Error fetching data after ${attempts} retries: ${error.message}`,
          )
        }
      }
    }
  }
  // If the function reaches this point, it means all attempts have failed
  throw new Error('Failed to fetch data from the API')
}
export default fetchDataFromAPI
